export { renderLicenseSnippet, renderLicenseSnippetLines }
export type { SnippetLine }

import type { LicenseMap } from './wire.js'

// One line of the paste-ready `pages/+config.js` snippet. `license: true` marks the lines that
// make up the `license: {…}` key — the lines a customer merges into an existing `+config` (the
// dashboard diff-highlights and copies just these); `false` is the `export default {}` wrapper,
// shown only as context.
type SnippetLine = { text: string; license: boolean }

// Structured form of the snippet — the single source of truth for its shape. Used by:
//   - the dashboard's `LicenseKeysPanel` (diff-highlights + copies the `license` lines),
//   - `renderLicenseSnippet` below (the flat-string form for the CLI + free-trial form).
//
// Any future format change (switching to `defineConfig`, renaming the `license` key) is one
// edit here. Handles both forever-valid (`string`) and time-limited (`{expires, key}`) entries;
// callers handing a `Record<string, string>` (dashboard's paid flavor) hit the string branch only.
function renderLicenseSnippetLines(license: LicenseMap): SnippetLine[] {
  const lines: SnippetLine[] = [
    { text: 'export default {', license: false },
    { text: '  license: {', license: true },
  ]
  for (const [domain, value] of Object.entries(license)) {
    const text =
      typeof value === 'string'
        ? `    '${domain}': '${value}',`
        : `    '${domain}': { expires: '${value.expires}', key: '${value.key}' },`
    lines.push({ text, license: true })
  }
  // Trailing comma so the copied `license: {…},` block slots cleanly into an existing config.
  lines.push({ text: '  },', license: true }, { text: '}', license: false })
  return lines
}

// Flat-string form for the maintainer `pnpm write:grant` CLI (prefixed for terminal output) and
// `<FreeTrialForm>`'s "Your license" panel (license-components, via the symlink at
// `packages/license-components/src/snippet.ts`).
function renderLicenseSnippet(license: LicenseMap): string {
  return renderLicenseSnippetLines(license)
    .map((line) => line.text)
    .join('\n')
}
